import React from 'react';
import { Box, Typography, Button, Container, Grid, useTheme, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { motion } from 'framer-motion';

const NotFound = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.3
      } 
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <Box 
      sx={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 6, md: 0 }
      }}
    >
      {/* Decorative elements */}
      <Box 
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          opacity: 0.4,
          background: `
            radial-gradient(circle at 10% 20%, rgba(0, 198, 207, 0.05) 0%, transparent 20%),
            radial-gradient(circle at 90% 80%, rgba(79, 172, 254, 0.05) 0%, transparent 20%),
            radial-gradient(circle at 50% 50%, rgba(0, 119, 182, 0.03) 0%, transparent 100%)
          `,
          zIndex: 0
        }}
      />
      
      {/* Animated dots */}
      {[...Array(6)].map((_, i) => (
        <Box
          key={i}
          component={motion.div}
          sx={{
            position: 'absolute',
            width: { xs: '150px', md: '300px' },
            height: { xs: '150px', md: '300px' },
            borderRadius: '50%',
            background: i % 2 === 0 ? 
              'radial-gradient(circle, rgba(0, 198, 207, 0.03) 0%, transparent 70%)' : 
              'radial-gradient(circle, rgba(79, 172, 254, 0.03) 0%, transparent 70%)',
            zIndex: 0,
            filter: 'blur(10px)'
          }}
          animate={{
            x: [Math.random() * 10, Math.random() * -10],
            y: [Math.random() * 10, Math.random() * -10],
          }}
          transition={{
            repeat: Infinity,
            repeatType: 'reverse',
            duration: 5 + Math.random() * 5,
          }}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
        />
      ))}
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          {/* Left side - 404 Text */}
          <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants}>
                <Typography 
                  variant="h1" 
                  sx={{ 
                    fontSize: { xs: '6rem', md: '10rem' },
                    fontWeight: 900,
                    lineHeight: 0.9,
                    background: 'linear-gradient(90deg, #0077be 0%, #00C6CF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 2,
                    letterSpacing: '-4px',
                    textShadow: '0 10px 30px rgba(0,0,0,0.05)'
                  }}
                >
                  404
                </Typography>
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                    fontWeight: 700,
                    color: '#263238',
                    mb: 2,
                    letterSpacing: '-0.5px'
                  }}
                >
                  Oops! Page not found
                </Typography>
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: { xs: '1rem', md: '1.125rem' },
                    color: '#546e7a',
                    mb: 4,
                    maxWidth: { xs: '100%', md: '80%' }
                  }}
                >
                  We can't seem to find the page you're looking for. It might have been moved, 
                  deleted, or perhaps never existed.
                </Typography>
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <Button 
                    component={Link} 
                    to="/"
                    variant="contained" 
                    size="large"
                    startIcon={<HomeIcon />}
                    sx={{ 
                      bgcolor: '#0077be',
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      boxShadow: '0 4px 14px rgba(0, 119, 190, 0.3)',
                      '&:hover': {
                        bgcolor: '#00669f',
                        boxShadow: '0 6px 20px rgba(0, 119, 190, 0.4)',
                      }
                    }}
                  >
                    Back to Home
                  </Button>
                  <Button 
                    onClick={() => window.history.back()}
                    variant="outlined" 
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    sx={{ 
                      color: '#0077be',
                      borderColor: '#0077be',
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#00669f',
                        bgcolor: 'rgba(0, 119, 190, 0.04)',
                      }
                    }}
                  >
                    Go Back
                  </Button>
                </Box>
              </motion.div>
            </motion.div>
          </Grid>
          
          {/* Right side - Illustration */}
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Box 
                sx={{ 
                  position: 'relative',
                  width: { xs: '280px', md: '400px' },
                  height: { xs: '280px', md: '400px' },
                }}
              >
                {/* Main circle */}
                <Box 
                  component={motion.div}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                    border: '15px solid rgba(255,255,255,0.7)',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                  animate={{ 
                    boxShadow: ['0 20px 50px rgba(0,0,0,0.08)', '0 20px 70px rgba(0,0,0,0.12)', '0 20px 50px rgba(0,0,0,0.08)'],
                  }}
                  transition={{ 
                    repeat: Infinity,
                    duration: 3,
                    repeatType: 'reverse'
                  }}
                  style={{
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  {/* Magnifying glass handle */}
                  <Box 
                    component={motion.div}
                    sx={{
                      position: 'absolute',
                      width: '60%',
                      height: '15%',
                      background: 'linear-gradient(90deg, #0077be 0%, #00C6CF 100%)',
                      borderRadius: '30px',
                      bottom: '5%',
                      right: '-15%',
                      transform: 'rotate(-45deg)',
                      transformOrigin: '0 50%',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                    }}
                    animate={{ 
                      boxShadow: ['0 10px 30px rgba(0,0,0,0.1)', '0 15px 40px rgba(0,0,0,0.15)', '0 10px 30px rgba(0,0,0,0.1)'],
                    }}
                    transition={{ 
                      repeat: Infinity,
                      duration: 3,
                      repeatType: 'reverse'
                    }}
                  />
                  
                  {/* Question mark */}
                  <Typography 
                    variant="h1" 
                    component={motion.h1}
                    sx={{ 
                      fontSize: { xs: '8rem', md: '12rem' },
                      fontWeight: 800,
                      color: '#0077be',
                      opacity: 0.3,
                      textShadow: '0 5px 15px rgba(0,0,0,0.1)',
                      userSelect: 'none'
                    }}
                    animate={{ 
                      y: [0, -10, 0],
                      opacity: [0.3, 0.4, 0.3],
                    }}
                    transition={{ 
                      repeat: Infinity,
                      duration: 3,
                      repeatType: 'reverse'
                    }}
                  >
                    ?
                  </Typography>
                </Box>
                
                {/* Decorative elements */}
                {[...Array(3)].map((_, i) => (
                  <Box
                    key={i}
                    component={motion.div}
                    sx={{
                      position: 'absolute',
                      width: 20 + i * 10,
                      height: 20 + i * 10,
                      borderRadius: '50%',
                      background: i % 2 === 0 ? 
                        'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' : 
                        'linear-gradient(135deg, #0077be 0%, #00C6CF 100%)',
                      opacity: 0.2 + (i * 0.1),
                      zIndex: 3 - i
                    }}
                    animate={{ 
                      x: [0, 20, 0, -20, 0],
                      y: [0, -20, 0, 20, 0],
                    }}
                    transition={{ 
                      repeat: Infinity,
                      duration: 5 + i * 2,
                      repeatType: 'loop'
                    }}
                    style={{
                      top: `${20 + i * 30}%`,
                      left: `${70 - i * 20}%`,
                    }}
                  />
                ))}
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default NotFound;
